<template>
    <Modal
        id="file-editor-modal"
        ref="fileEditorModal"
        v-model:open="open"
        :title="title"
        width="4xl"
        @submit="submit"
        @cancel="cancel"
    >
        <div
            id="viewer"
            ref="viewer"
            class="h-[80vh] w-full"
        ></div>
    </Modal>
</template>

<script setup lang="ts">
import WebViewer from '@pdftron/webviewer'
import { Modal } from '@src/components'
import { Placeholder } from '@src/types/Placeholder'
import { useVModels } from '@vueuse/core'
import Cookies from 'js-cookie'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
    defineProps<{
        open: boolean
        url: string
        title: string
        placeholders?: Placeholder[] | null
        fileName: string | null
    }>(),
    {
        open: false,
        title: 'edit_file',
        placeholders: null,
        fileName: null,
    }
)

const emit = defineEmits(['update:open', 'cancel', 'submit'])

const { open } = useVModels(props, emit)
const { t } = useI18n()

const viewer = ref()

const placeholders = ref<Placeholder[] | null>(null)
placeholders.value = props.placeholders ?? null

function cancel() {
    emit('cancel')
}

function submit() {
    emit('submit')
}

// File editor
onMounted(() => {
    WebViewer(
        {
            path: '/webviewer',
            licenseKey: import.meta.env.VITE_WEBVIEWER_FILE_EDITOR_LICENSE_KEY,
            fullAPI: true,
            initialDoc: props.url,
            useDownloader: false,
            // This disables trying to load .gz.js
            streaming: false,

            // Disable elements for a cleaner UI
            disabledElements: [
                'menuButton',
                'leftPanelButton',
                'viewControlsButton',
                'toolbarGroup-View',
                'toolbarGroup-Shapes',
                'toolbarGroup-Edit',
                'toolbarGroup-Insert',
                'toolbarGroup-FillAndSign',
                'toolbarGroup-Annotate',
                'toolbarGroup-Forms',
                'toggleNotesButton',
                'notesPanel',
                'moreButton',
            ],
        },
        viewer.value
    ).then(function (instance) {
        const { documentViewer, Annotations, annotationManager, PDFNet } =
            instance.Core
        const document = instance.UI.iframeWindow.document
        instance.UI.setTheme('light')
        instance.UI.setLanguage(Cookies.get('language') || 'en')

        instance.UI.setHeaderItems((header) => {
            // Custom button to toggle the placeholders panel
            header.push({
                type: 'toggleElementButton',
                dataElement: 'customPanelToggle',
                toggleElement: 'placeholdersPanel',
                title: t('show_placeholders_panel'),
                label: t('placeholders'),
                style: {
                    padding: '1em 4em',
                },
                onClick: () => {
                    const isOpen =
                        instance.UI.isElementOpen('placeholdersPanel')
                    if (isOpen) {
                        instance.UI.closeElements(['placeholdersPanel'])
                    } else {
                        instance.UI.openElements(['placeholdersPanel'])
                    }
                },
            })
        })

        // Check if the file already has fields that match the placeholders, if so fill them in
        documentViewer.addEventListener('annotationsLoaded', () => {
            const annotations = annotationManager.getAnnotationsList()
            annotations.forEach((annotation) => {
                if (annotation instanceof Annotations.TextWidgetAnnotation) {
                    if (placeholders.value && placeholders.value.length > 0) {
                        const placeholder = placeholders.value.find(
                            (placeholder) =>
                                placeholder.key === annotation.getField().name
                        )
                        if (placeholder) {
                            annotation.getField().setValue(placeholder.value)
                        }
                        // Set the annotation as listable (makes it selectable)
                        annotation.Listable = true
                    }
                }
                annotationManager.redrawAnnotation(annotation)
            })
        })

        documentViewer.addEventListener('documentLoaded', () => {
            // Custom panel for Placeholders
            instance.UI.addPanel({
                dataElement: 'placeholdersPanel',
                location: 'right',
                render: () => {
                    const div = document.createElement('div')
                    div.style.padding = '1em'

                    if (placeholders.value && placeholders.value.length > 0) {
                        // Add `flatten signature placeholder` button
                        const flattenSignaturePlaceholderButton =
                            document.createElement('button')
                        flattenSignaturePlaceholderButton.textContent = t(
                            'flatten_signature_placeholder_button'
                        )

                        flattenSignaturePlaceholderButton.title = t(
                            'flatten_signature_placeholder_button_info'
                        )

                        Object.assign(flattenSignaturePlaceholderButton.style, {
                            padding: '1em',
                            width: '100%',
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            backgroundColor: 'blue',
                            color: 'white',
                        })

                        flattenSignaturePlaceholderButton.addEventListener(
                            'click',
                            async () => {
                                await PDFNet.initialize()

                                const doc = await documentViewer
                                    .getDocument()
                                    .getPDFDoc()

                                // export annotations from the document
                                const annots =
                                    await annotationManager.exportAnnotations()

                                // Run PDFNet methods with memory management
                                await PDFNet.runWithCleanup(
                                    async () => {
                                        // lock the document before a write operation
                                        // runWithCleanup will auto unlock when complete
                                        doc.lock()

                                        // import annotations to PDFNet
                                        const fdf_doc =
                                            await PDFNet.FDFDoc.createFromXFDF(
                                                annots
                                            )
                                        await doc.fdfUpdate(fdf_doc)

                                        // Get the signature placeholder annotation
                                        const annotation = annotationManager
                                            .getAnnotationsList()
                                            .find(
                                                (annotation) =>
                                                    annotation instanceof
                                                        Annotations.TextWidgetAnnotation &&
                                                    placeholders.value?.some(
                                                        (placeholder) =>
                                                            placeholder.key
                                                                .toLowerCase()
                                                                .trim() ===
                                                                annotation
                                                                    .getField()
                                                                    ?.name.toLowerCase()
                                                                    .trim() &&
                                                            placeholder.type ===
                                                                'signature'
                                                    )
                                            )

                                        // Get page number of the annotation
                                        const page = await doc.getPage(
                                            annotation?.getPageNumber() ?? 1
                                        )

                                        // Get thhe annotation (annot) from PDFNet
                                        if (annotation) {
                                            const annot = await page.getAnnot(
                                                Number(annotation.Id)
                                            )

                                            if (annot) {
                                                // Flatten the annotation
                                                await annot.flatten(page)
                                                console.log(
                                                    'Annotation flattened'
                                                )
                                            }
                                        }

                                        // clear the original annotations
                                        annotationManager.deleteAnnotations(
                                            annotationManager.getAnnotationsList()
                                        )

                                        // import annotations from PDFNet
                                        const fdfDoc = await doc.fdfExtract(
                                            PDFNet.PDFDoc.ExtractFlag.e_both
                                        )
                                        const xfdf =
                                            await fdfDoc.saveAsXFDFAsString()
                                        annotationManager.importAnnotations(
                                            xfdf
                                        )
                                    },
                                    // license key
                                    import.meta.env.VITE_WEBVIEWER_LICENSE_KEY
                                )

                                // clear the cache (rendered) data with the newly updated document
                                documentViewer.refreshAll()

                                // Update viewer to render with the new document
                                documentViewer.updateView()

                                // Refresh searchable and selectable text data with the new document
                                documentViewer.getDocument().refreshTextData()
                            }
                        )

                        const signaturePlaceholderDivider =
                            document.createElement('hr')

                        // Add `apply placeholder` button
                        const applyPlaceholdersButton =
                            document.createElement('button')
                        applyPlaceholdersButton.textContent =
                            t('apply_placeholders')

                        applyPlaceholdersButton.title = t(
                            'apply_placeholders_info'
                        )
                        Object.assign(applyPlaceholdersButton.style, {
                            padding: '1em',
                            width: '100%',
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            backgroundColor: '#398A71',
                            color: 'white',
                        })

                        applyPlaceholdersButton.addEventListener(
                            'click',
                            async () => {
                                // Remove all values from the placeholder annotations, so only empty fields with field names remain
                                const annotations =
                                    annotationManager.getAnnotationsList()
                                annotations.forEach((annotation) => {
                                    if (
                                        annotation instanceof
                                        Annotations.WidgetAnnotation
                                    ) {
                                        if (placeholders.value) {
                                            // Check if the field name is a placeholder
                                            const placeholder =
                                                placeholders.value.find(
                                                    (placeholder: {
                                                        key: any
                                                    }) =>
                                                        String(placeholder.key)
                                                            .toLowerCase()
                                                            .trim() ===
                                                        String(
                                                            annotation.getField()
                                                                .name
                                                        )
                                                            .toLowerCase()
                                                            .trim()
                                                )
                                            if (placeholder) {
                                                // Clear the value of the field
                                                annotation
                                                    .getField()
                                                    .setValue('')
                                            }
                                        }
                                    }
                                })

                                const doc = documentViewer.getDocument()
                                const xfdfString =
                                    await annotationManager.exportAnnotations()
                                const data = await doc.getFileData({
                                    // saves the document with annotations in it
                                    xfdfString,
                                })
                                const arr = new Uint8Array(data)
                                const blob = new Blob([arr], {
                                    type: 'application/pdf',
                                })

                                const fileName =
                                    props.fileName ?? 'default_filename.pdf'
                                const file = new File([blob], fileName, {
                                    type: blob.type,
                                })

                                // Load the new file
                                const newUrl = URL.createObjectURL(file)
                                documentViewer.loadDocument(newUrl)

                                // Close editor Modal
                                emit('submit', file)
                            }
                        )

                        div.appendChild(applyPlaceholdersButton)

                        // Add divider line
                        const divider = document.createElement('hr')
                        div.appendChild(divider)

                        placeholders.value.forEach((placeholder) => {
                            // Show each placeholder as a button
                            const placeholderButton =
                                document.createElement('div')
                            Object.assign(placeholderButton.style, {
                                marginBottom: '1em',
                                padding: '1em',
                                minWidth: '100%',
                                border: '1px solid #ccc',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                backgroundColor: '#F1F3F5',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            })

                            const key = document.createElement('strong')
                            key.textContent = `${placeholder.key}`
                            placeholderButton.appendChild(key)

                            if (placeholder.type === 'signature') {
                                // Show the 'Flatten signature button'
                                div.appendChild(
                                    flattenSignaturePlaceholderButton
                                )

                                placeholderButton.appendChild(key).title = t(
                                    'signature_placeholder_info'
                                )
                                div.appendChild(signaturePlaceholderDivider)
                            } else if (placeholder.type === 'qr-code') {
                                placeholderButton.title = t(
                                    'qr_code_placeholder_info'
                                )
                            } else {
                                placeholderButton.title = t(
                                    'standard_placeholder_info'
                                )
                            }

                            // Placeholder button click event to add a placeholder to the document
                            placeholderButton.addEventListener('click', () => {
                                // Remove existing QR code placeholder if any
                                const annotations =
                                    annotationManager.getAnnotationsList()
                                annotations.forEach((annotation) => {
                                    if (
                                        annotation instanceof
                                            Annotations.WidgetAnnotation &&
                                        annotation.getField().name ===
                                            placeholder.key &&
                                        placeholder.type === 'qr-code'
                                    ) {
                                        annotationManager.deleteAnnotation(
                                            annotation
                                        )
                                    }
                                })

                                const placeholderField =
                                    new Annotations.Forms.Field(
                                        placeholder.key,
                                        {
                                            type: 'Tx',
                                            value:
                                                placeholder.type === 'qr-code'
                                                    ? 'QR-code'
                                                    : placeholder.value,
                                        }
                                    )

                                const placeholderTextWidgetAnnotation =
                                    new Annotations.TextWidgetAnnotation(
                                        placeholderField,
                                        {}
                                    )

                                // Set the annotation as listable (makes it selectable)
                                placeholderTextWidgetAnnotation.Listable = true

                                // Change the appearance of the annotation
                                placeholderTextWidgetAnnotation.PageNumber =
                                    documentViewer.getCurrentPage()
                                placeholderTextWidgetAnnotation.X =
                                    documentViewer.getPageWidth(
                                        documentViewer.getCurrentPage()
                                    ) / 2
                                placeholderTextWidgetAnnotation.Y =
                                    documentViewer.getPageHeight(
                                        documentViewer.getCurrentPage()
                                    ) / 2
                                placeholderTextWidgetAnnotation.font =
                                    new Annotations.Font({
                                        name: 'Helvetica',
                                    })
                                placeholderTextWidgetAnnotation.font.size = 14

                                if (
                                    placeholder.type &&
                                    placeholder.type === 'qr-code'
                                ) {
                                    // Styling for QR code
                                    placeholderTextWidgetAnnotation.Width = 80
                                    placeholderTextWidgetAnnotation.Height = 80
                                    placeholderTextWidgetAnnotation.NoResize =
                                        true
                                    placeholderTextWidgetAnnotation.LockedContents =
                                        true
                                } else {
                                    // Styling for normal text field
                                    if (
                                        placeholder.value &&
                                        placeholder.value.length > 15
                                    ) {
                                        placeholderTextWidgetAnnotation.Width = 150
                                    } else {
                                        placeholderTextWidgetAnnotation.Width = 100
                                    }
                                    placeholderTextWidgetAnnotation.Height = 20
                                }

                                // Add the annotation to the document
                                annotationManager
                                    .getFieldManager()
                                    .addField(placeholderField)
                                annotationManager.addAnnotation(
                                    placeholderTextWidgetAnnotation
                                )
                                annotationManager.drawAnnotationsFromList([
                                    placeholderTextWidgetAnnotation,
                                ])

                                // Set the new annotation as selected
                                annotationManager.deselectAllAnnotations()
                                annotationManager.selectAnnotation(
                                    placeholderTextWidgetAnnotation
                                )
                            })

                            div.appendChild(placeholderButton)
                        })
                    } else {
                        const noPlaceholders = document.createElement('p')
                        const noPlaceholdersText = t(
                            'no_placeholders_available'
                        )
                        noPlaceholders.textContent = noPlaceholdersText
                        div.appendChild(noPlaceholders)
                    }
                    return div
                },
            })
        })
    })
})
</script>
